import { Card, Button, Spinner } from '@agentisai/design-system';
import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { useAuth } from '../auth/AuthProvider';
import { parseInvitation, stripInvitationParams } from '../auth/invitation';
import { safeNextPath } from '../router/safeNextPath';
import * as styles from './LoginPage.css';

/**
 * LoginPage — public. The on-brand login landing (S-E4): a centred brand
 * lockup over a 420px auth card, matching the agentis2 prototype's
 * `#screen-login`. Reads a validated ?next= return path and seeds it into
 * the Auth0 login (appState.returnTo) so post-callback lands back there.
 * Already-authenticated visitors are redirected home.
 *
 * An Auth0 invitation link (D15-14) arrives as ?invitation=&organization=; when
 * both are well-formed the CTA passes them to the authorize call and the three
 * params are stripped from the address bar first. They are captured once into
 * state so a retry still has them. A visitor already signed in keeps the
 * invitation unconsumed: they get a log-out prompt instead of a silent sign-in.
 *
 * Auth0 hosted login is the real auth — there is no demo-account picker,
 * password field, or "Forgot password?" here; the single "Log in" CTA hands
 * off to Auth0.
 */
export function LoginPage() {
  const { isLoading, isAuthenticated, error, login, logout, user } = useAuth();
  const [params] = useSearchParams();
  const next = safeNextPath(params.get('next'));
  const [invite] = useState(() => parseInvitation(params));
  const signIn = () => {
    if (invite) stripInvitationParams();
    login(next, invite);
  };

  if (isLoading) {
    return (
      <main className={styles.page}>
        <Spinner size="lg" tone="primary" label="Loading…" />
      </main>
    );
  }

  if (isAuthenticated && !invite) {
    return <Navigate to="/" replace />;
  }

  return (
    <main className={styles.page}>
      <div className={styles.lockup}>
        <span className={styles.brand}>Agentis Ai</span>
        <span className={styles.product}>GENESIS</span>
      </div>
      <Card elevation={1} padding="lg" className={styles.card}>
        {isAuthenticated ? (
          <>
            <h1 className={styles.title}>You&apos;re signed in as someone else</h1>
            <p className={styles.body}>
              This invitation is for another account
              {user?.email ? `, and you are signed in as ${user.email}` : ''}. Log out, then open
              the invitation link again to accept it.
            </p>
            <Button variant="primary" size="lg" fullWidth onClick={logout}>
              Log out
            </Button>
          </>
        ) : error ? (
          <>
            <h1 className={styles.title}>Sign-in error</h1>
            <p className={styles.body}>{error.message}</p>
            <Button variant="primary" size="lg" fullWidth onClick={signIn}>
              Try again
            </Button>
          </>
        ) : (
          <>
            <h1 className={styles.title}>Welcome to Agentis AI Genesis</h1>
            <p className={styles.subtitle}>Sign in to continue</p>
            <p className={styles.body}>
              {invite?.organizationName
                ? `You've been invited to join ${invite.organizationName}. Sign in or create your account to accept.`
                : 'Authentication is handled by Auth0. Sign in to access your workstreams.'}
            </p>
            <Button variant="primary" size="lg" fullWidth onClick={signIn}>
              Log in
            </Button>
          </>
        )}
      </Card>
    </main>
  );
}
LoginPage.displayName = 'LoginPage';
